import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { postApi } from "../services/api/api";
import { storeData } from "../services/localStorageService";
import { fetchFcmToken } from "../services/firebaseConfig";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const RegistrationModal = () => {
    const navigate = useNavigate();
    const modalRef = useRef(null);
    const [step, setStep] = useState("register");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [country, setCountry] = useState("in");
    const [formData, setFormData] = useState({
        name: "",
        l_name: "",
        country_code: "+91",
        mobile: "",
        email: "",
        password: "",
        password_confirmation: "",
        user_type: 1,
        firebase_token: "",
        terms_accepted: false
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [otp, setOtp] = useState(["", "", "", ""]);
    const otpRefs = useRef([]);
    const [timer, setTimer] = useState(170);
    const [resendCount, setResendCount] = useState(0);
    const timerRef = useRef(null);

    useEffect(() => {
        const loadNotificationToken = async () => {
            try {
                const token = await fetchFcmToken();
                if (token) {
                    setFormData(prev => ({ ...prev, firebase_token: token }));
                } else {
                    setFormData(prev => ({ ...prev, firebase_token: "browser_notification_blocked_or_failed" }));
                }
            } catch (err) {
                console.error("Initial FCM Token retrieval blocked or failed:", err);
                setFormData(prev => ({ ...prev, firebase_token: "browser_notification_blocked_or_failed" }));
            }
        };
        loadNotificationToken();
    }, []);

    const resetForm = () => {
        setStep("register");
        setShowPassword(false);
        setShowConfirmPassword(false);
        setCountry("in");
        setFormData(prev => ({
            name: "",
            l_name: "",
            country_code: "+91",
            mobile: "",
            email: "",
            password: "",
            password_confirmation: "",
            user_type: 1,
            firebase_token: prev.firebase_token,
            terms_accepted: false
        }));
        setErrors({});
        setOtp(["", "", "", ""]);
        setTimer(170);
        setResendCount(0);
        setLoading(false);
        if (timerRef.current) clearInterval(timerRef.current);
    };

    useEffect(() => {
        const modalElement = modalRef.current;

        const handleModalShown = () => {
            setCountry("in");
            setFormData(prev => ({ ...prev, country_code: "+91", mobile: "" }));
        };

        const handleModalHidden = () => {
            resetForm();
        };

        if (modalElement) {
            modalElement.addEventListener("shown.bs.modal", handleModalShown);
            modalElement.addEventListener("hidden.bs.modal", handleModalHidden);
        }
        return () => {
            if (modalElement) {
                modalElement.removeEventListener("shown.bs.modal", handleModalShown);
                modalElement.removeEventListener("hidden.bs.modal", handleModalHidden);
            }
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, []);

    useEffect(() => {
        if (step === "otp" && timer > 0) {
            timerRef.current = setInterval(() => { setTimer((prevTime) => prevTime - 1); }, 1000);
        } else if (timer === 0) {
            clearInterval(timerRef.current);
        }
        return () => clearInterval(timerRef.current);
    }, [step, timer]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let targetValue = type === "checkbox" ? checked : value;
        if (name === "name" || name === "l_name") {
            if (typeof targetValue === "string") {
                targetValue = targetValue.replace(/\b\w/g, (char) => char.toUpperCase());
            }
        }
        setFormData({ ...formData, [name]: targetValue });
        if (errors[name]) setErrors({ ...errors, [name]: "" });
    };

    const handlePhoneChange = (value, countryData) => {
        if (errors.mobile) setErrors(prev => ({ ...prev, mobile: "" }));

        if (!value) {
            setFormData(prev => ({ ...prev, mobile: "" }));
            return;
        }

        const dialCode = countryData.dialCode; // e.g. "91"
        const countryIso = countryData.countryCode; // e.g. "in"
        setCountry(countryIso);
        let nationalNumber = value;
        if (value.startsWith(dialCode)) {
            nationalNumber = value.slice(dialCode.length);
        }

        setFormData(prev => ({
            ...prev,
            country_code: `+${dialCode}`,
            mobile: nationalNumber
        }));
    };

    const handleOtpChange = (value, index) => {
        if (!/^[0-9]?$/.test(value)) return;
        const updatedOtp = [...otp];
        updatedOtp[index] = value;
        setOtp(updatedOtp);
        if (value && index < 3) { otpRefs.current[index + 1].focus(); }
    };

    const handleOtpKeyDown = (e, index) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) { otpRefs.current[index - 1].focus(); }
    };

    const validate = () => {
        let tempErrors = {};
        const emailRegex = /\S+@\S+\.\S+/;
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
        const alphabetRegex = /^[A-Za-z\s]+$/;

        if (!formData.name.trim()) {
            tempErrors.name = "First name is required";
        } else if (!alphabetRegex.test(formData.name)) {
            tempErrors.name = "First name must contain alphabets only";
        } else if (formData.name.length > 50) {
            tempErrors.name = "First name cannot exceed 50 characters";
        } else if (formData.name.trim().length < 3) {
            tempErrors.name = "First name must be at least 3 characters";
        }

        if (!formData.l_name.trim()) {
            tempErrors.l_name = "Last name is required";
        } else if (!alphabetRegex.test(formData.l_name)) {
            tempErrors.l_name = "Last name must contain alphabets only";
        } else if (formData.l_name.length > 50) {
            tempErrors.l_name = "Last name cannot exceed 50 characters";
        } else if (formData.l_name.trim().length < 3) {
            tempErrors.l_name = "Last name must be at least 3 characters";
        }

        const rawNumberString = formData.mobile.trim();

        if (!rawNumberString) {
            tempErrors.mobile = "Mobile number is required";
        } else if (formData.country_code === "+91") {
            if (rawNumberString.length !== 10) {
                tempErrors.mobile = "Indian mobile number must be exactly 10 digits";
            } else if (!/^[6-9]/.test(rawNumberString)) {
                tempErrors.mobile = "Indian mobile number must start with 6, 7, 8, or 9";
            }
        } else if (rawNumberString.length < 6 || rawNumberString.length > 15) {
            tempErrors.mobile = "International phone number length must be between 6 and 15 digits";
        }

        if (!formData.email.trim()) {
            tempErrors.email = "Email is required";
        } else if (!emailRegex.test(formData.email)) {
            tempErrors.email = "Please enter a valid email address";
        }

        if (!formData.password) {
            tempErrors.password = "Password is required";
        } else if (formData.password.length > 50) {
            tempErrors.password = "Password cannot exceed 50 characters";
        } else if (!passwordRegex.test(formData.password)) {
            tempErrors.password = "Password must be 8+ characters with uppercase, lowercase, number, and special character";
        }

        if (!formData.password_confirmation) {
            tempErrors.password_confirmation = "Please confirm your password";
        } else if (formData.password_confirmation.length > 50) {
            tempErrors.password_confirmation = "Confirmation entry cannot exceed 50 characters";
        } else if (formData.password !== formData.password_confirmation) {
            tempErrors.password_confirmation = "Passwords do not match";
        }

        if (!formData.terms_accepted) { tempErrors.terms_accepted = "You must accept the terms and conditions"; }

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleRegistrationSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setLoading(true);

        let activeToken = formData.firebase_token;
        const { terms_accepted, ...apiPayload } = formData;

        try {
            const response = await postApi('register', apiPayload);
            if (response.code === 200 || response.status === 200 || response.success) {
                setStep("otp");
                setTimer(170);
                setResendCount(0);
                setErrors({});
            } else {
                setErrors({ server: response.message || "Registration failed" });
            }
        } catch (error) {
            setErrors({ server: error.response?.data?.message || "Connection failed. Try again." });
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        const fullOtp = otp.join("");
        if (fullOtp.length < 4) { setErrors({ otp: "Please enter 4-digit OTP" }); return; }

        setLoading(true);
        try {
            const response = await postApi('verify-otp', {
                country_code: formData.country_code,
                mobile: formData.mobile,
                otp: fullOtp,
                firebase_token: formData.firebase_token
            });
            if (response.code === 200 || response.success) {
                storeData('token', response.data.token);
                storeData('userDetails', JSON.stringify(response.data.user_details));
                const modalInstance = window.bootstrap?.Modal.getInstance(modalRef.current);
                if (modalInstance) modalInstance.hide();
                navigate("/CreateProfile");
            } else {
                setErrors({ otp: response.message || "Invalid OTP" });
            }
        } catch (error) {
            setErrors({ otp: error.response?.data?.message || "Verification failed." });
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (resendCount >= 3) return;
        setLoading(true);
        try {
            const response = await postApi('send-otp', { country_code: formData.country_code, mobile: formData.mobile });
            if (response.code === 200 || response.success) {
                setOtp(["", "", "", ""]); setTimer(170); setResendCount((prev) => prev + 1); setErrors({});
            }
        } catch (error) { console.error(error); } finally { setLoading(false); }
    };

    const phoneInputValue = `${formData.country_code.replace("+", "")}${formData.mobile}`;

    return (
        <div ref={modalRef} className="modal fade" id="registrationModal" data-bs-backdrop="static" data-bs-keyboard="false" tabIndex="-1" aria-labelledby="registrationModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content">
                    <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close">
                        <img src={`${process.env.PUBLIC_URL}/assets/imgs/close_icon.png`} alt="close" />
                    </button>

                    <div className="modal-body p-0">
                        <div className='row'>
                            <div className='col-md-5'>
                                <div className='login_regi_left'>
                                    <h4>Now</h4>
                                    <h1>Find your life partner</h1>
                                    <h4>Easy and fast.</h4>
                                    <div className='log-bg'></div>
                                </div>
                            </div>
                            <div className='col-md-7 center_c'>
                                <div className='right_content'>

                                    {step === "register" && (
                                        <div className="register_d">
                                            <div className='log_re_heading'>
                                                <h2>Sign up to Matrimony</h2>
                                                <p>Already a member? <a data-bs-toggle="modal" data-bs-target="#loginModal" style={{cursor: 'pointer'}}>Login</a></p>
                                            </div>

                                            {errors.server && <div className="alert alert-danger p-1 small">{errors.server}</div>}

                                            <form onSubmit={handleRegistrationSubmit}>
                                                <div className='row'>
                                                    <div className='col-md-6'>
                                                        <div className='inputcontainer'>
                                                            <h5 className='input_label'>First Name<span className='m_feild'>*</span></h5>
                                                            <input name="name" maxLength="50" value={formData.name} onChange={handleChange} className={`form-control input_style capitalize ${errors.name ? 'is-invalid' : ''}`} placeholder='Enter First Name' />
                                                            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                                        </div>
                                                    </div>
                                                    <div className='col-md-6'>
                                                        <div className='inputcontainer'>
                                                            <h5 className='input_label'>Last Name<span className='m_feild'>*</span></h5>
                                                            <input name="l_name" maxLength="50" value={formData.l_name} onChange={handleChange} className={`form-control input_style ${errors.l_name ? 'is-invalid' : ''}`} placeholder='Enter Last Name' />
                                                            {errors.l_name && <div className="invalid-feedback">{errors.l_name}</div>}
                                                        </div>
                                                    </div>

                                                    <div className='col-md-12'>
                                                        <div className="inputcontainer mb-3 global-phone-wrapper-override">
                                                            <h5 className='input_label'>Mobile Number<span className='m_feild'>*</span></h5>
                                                            <PhoneInput
                                                                country={country}
                                                                value={phoneInputValue}
                                                                onChange={(val, data) => handlePhoneChange(val, data)}
                                                                placeholder="Enter Mobile Number"
                                                                inputProps={{
                                                                    name: 'mobile',
                                                                    required: true,
                                                                }}
                                                                containerClass="phone-input-container"
                                                                inputClass={`form-control input_style ${errors.mobile ? "is-invalid" : ""}`}
                                                            />
                                                            {errors.mobile && <div className="text-danger small mt-1" style={{ fontSize: "12px" }}>{errors.mobile}</div>}
                                                        </div>
                                                    </div>

                                                    <div className='col-md-12'>
                                                        <div className='inputcontainer'>
                                                            <h5 className='input_label'>Email<span className='m_feild'>*</span></h5>
                                                            <input name="email" value={formData.email} onChange={handleChange} className={`form-control input_style ${errors.email ? 'is-invalid' : ''}`} placeholder='Enter Email' />
                                                            {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                                                        </div>
                                                    </div>

                                                    <div className='col-md-6'>
                                                        <div className='inputcontainer'>
                                                            <h5 className='input_label'>Password<span className='m_feild'>*</span></h5>
                                                            <div className="position-relative">
                                                                <input type={showPassword ? "text" : "password"} name="password" maxLength="50" value={formData.password} onChange={handleChange} className={`form-control input_style ${errors.password ? 'is-invalid' : ''}`} placeholder='Enter Password' style={{ paddingRight: "40px" }} />
                                                                <span onClick={() => setShowPassword(!showPassword)} style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", cursor: "pointer", zIndex: 10, color: "#777" }}><i className={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`}></i></span>
                                                            </div>
                                                            {errors.password && <div className="text-danger small mt-1" style={{ fontSize: "12px" }}>{errors.password}</div>}
                                                        </div>
                                                    </div>

                                                    <div className='col-md-6'>
                                                        <div className='inputcontainer'>
                                                            <h5 className='input_label'>Confirm Password<span className='m_feild'>*</span></h5>
                                                            <div className="position-relative">
                                                                <input type={showConfirmPassword ? "text" : "password"} name="password_confirmation" maxLength="50" value={formData.password_confirmation} onChange={handleChange} className={`form-control input_style ${errors.password_confirmation ? 'is-invalid' : ''}`} placeholder='Enter Confirm Password' style={{ paddingRight: "40px" }} />
                                                                <span onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", cursor: "pointer", zIndex: 10, color: "#777" }}><i className={`fa ${showConfirmPassword ? "fa-eye-slash" : "fa-eye"}`}></i></span>
                                                            </div>
                                                            {errors.password_confirmation && <div className="text-danger small mt-1" style={{ fontSize: "12px" }}>{errors.password_confirmation}</div>}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className='d-flex flex-column inputcontainer'>
                                                    <div className="form-check m-0">
                                                        <input className={`form-check-input ${errors.terms_accepted ? 'is-invalid' : ''}`} type="checkbox" name="terms_accepted" checked={formData.terms_accepted} onChange={handleChange} id="termsCheckbox" />
                                                        <label className="form-check-label" htmlFor="termsCheckbox">I accept the <a href='/TermsConditions' target="_blank">Terms and Conditions</a> of this website</label>
                                                    </div>
                                                    {errors.terms_accepted && <small className="text-danger" style={{fontSize: '12px'}}>{errors.terms_accepted}</small>}
                                                </div>

                                                <button type="submit" className='btn next-btn w-100' disabled={loading}>{loading ? "Registering..." : "Sign up"}</button>
                                            </form>
                                        </div>
                                    )}

                                    {step === "otp" && (
                                        <div className="verify_otp">
                                            <div className="log_re_heading">
                                                <h2>Verify OTP</h2>
                                                <p>We have sent a code to your mobile number <strong>{formData.country_code} {formData.mobile}</strong>.</p>
                                            </div>
                                            {errors.otp && <div className="alert alert-danger p-2 small">{errors.otp}</div>}
                                            <form onSubmit={handleVerifyOtp}>
                                                <div className="inputcontainer">
                                                    <h5 className="input_label">Enter OTP<span className="m_feild">*</span></h5>
                                                    <div className="d-flex gap-2 justify-content-center">
                                                        {otp.map((digit, index) => (
                                                            <input key={index} type="text" inputMode="numeric" maxLength="1" value={digit} ref={(el) => (otpRefs.current[index] = el)} onChange={(e) => handleOtpChange(e.target.value, index)} onKeyDown={(e) => handleOtpKeyDown(e, index)} className="form-control otp_input" style={{textAlign: 'center' }} />
                                                        ))}
                                                    </div>
                                                </div>
                                                <div className="inputcontainer text-center">
                                                    {timer > 0 ? <p className="otp_ex_txt">Resend OTP after <strong>{formatTime(timer)}</strong></p> : <p className="text-danger small fw-bold">Resend OTP.</p>}
                                                </div>
                                                <button type="submit" className="btn next-btn w-100" disabled={loading || timer === 0}>{loading ? "Verifying..." : "Verify & Register"}</button>
                                                <div className="text-center mt-2">
                                                    {resendCount < 3 ? <button type="button" className="btn trans_btn w-100 border-0" style={{ textDecoration: 'underline' }} onClick={handleResendOtp} disabled={timer > 0 || loading}>Resend OTP {resendCount > 0 && `(${3 - resendCount} attempts left)`}</button> : <small className="text-muted d-block">Maximum resend limit reached.</small>}
                                                </div>
                                                <button type="button" className="btn trans_btn w-100" onClick={() => setStep("register")}>Back to Registration</button>
                                            </form>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RegistrationModal;